// src/pages/Admin/Photos/components/PhotosList.jsx
import React from "react";
import {
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiFileText,
  FiImage,
  FiTrash2,
} from "react-icons/fi";

import {
  formatFileSize,
  isImageMedia,
  isPdfMedia,
  toAbsolutePublicUrl,
} from "../../../../services/mediaLibraryServices";

const formatDate = (iso) => {
  if (!iso) return "";

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) return "";

    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const getMediaLabel = (item) => {
  if (isImageMedia(item)) return "Image";
  if (isPdfMedia(item)) return "PDF";
  return item.type || "Fichier";
};

export default function PhotosList({
  items,
  loading,
  total,
  page,
  totalPages,
  setPage,
  onPreview,
  onEdit,
  onAskDelete,
}) {
  const gotoPrev = () => setPage((p) => Math.max(p - 1, 1));
  const gotoNext = () => setPage((p) => Math.min(p + 1, totalPages));

  const openInNewTab = (item) => {
    const url = toAbsolutePublicUrl(item?.url);

    if (!url) return;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mt-5">
      <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-5">
        {loading && (
          <div className="flex min-h-[220px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-sky-600 dark:border-slate-700 dark:border-t-sky-400" />
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                Chargement des médias…
              </p>
            </div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="flex min-h-[220px] items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950/50">
            <div>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-500 shadow-sm dark:bg-slate-900">
                <FiImage className="h-7 w-7" />
              </div>
              <p className="text-sm font-bold text-slate-800 dark:text-white">
                Aucun média trouvé
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Essayez d’élargir vos filtres ou ajoutez une image ou un PDF.
              </p>
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {items.map((item) => {
              const url = toAbsolutePublicUrl(item.url);
              const image = isImageMedia(item);
              const pdf = isPdfMedia(item);
              const mediaLabel = getMediaLabel(item);

              return (
                <article
                  key={item.id}
                  className="group overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-950/70"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-200 dark:bg-slate-800">
                    {image ? (
                      <img
                        src={url}
                        alt={item.alt_text || item.title || item.file_name}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : pdf ? (
                      <button
                        type="button"
                        onClick={() => openInNewTab(item)}
                        className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-red-50 via-white to-slate-100 p-5 text-center transition hover:from-red-100 dark:from-red-950/30 dark:via-slate-950 dark:to-slate-900"
                        title="Ouvrir le PDF"
                      >
                        <span className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-600 text-white shadow-lg shadow-red-600/20">
                          <FiFileText className="h-8 w-8" />
                        </span>
                        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700 dark:bg-red-950 dark:text-red-300">
                          Document PDF
                        </span>
                        <span className="mt-2 line-clamp-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                          {item.title || item.original_name || item.file_name}
                        </span>
                      </button>
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center bg-slate-100 p-5 text-center dark:bg-slate-900">
                        <span className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-800 text-white dark:bg-slate-700">
                          <FiFileText className="h-8 w-8" />
                        </span>
                        <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          Fichier
                        </span>
                      </div>
                    )}

                    <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-950/75 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur">
                        {image ? (
                          <FiImage className="h-3 w-3" />
                        ) : (
                          <FiFileText className="h-3 w-3" />
                        )}
                        {mediaLabel}
                      </span>

                      <span
                        className={
                          "inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide backdrop-blur " +
                          (item.is_public
                            ? "bg-emerald-500/90 text-white"
                            : "bg-amber-500/90 text-white")
                        }
                      >
                        {item.is_public ? "Public" : "Privé"}
                      </span>
                    </div>

                    <div className="absolute right-3 top-3 flex gap-1.5 opacity-0 transition group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => onPreview(item)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-sm transition hover:bg-sky-50 hover:text-sky-700"
                        title="Prévisualiser"
                      >
                        <FiEye className="h-4 w-4" />
                      </button>

                      {(pdf || !image) && (
                        <button
                          type="button"
                          onClick={() => openInNewTab(item)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-sm transition hover:bg-sky-50 hover:text-sky-700"
                          title="Ouvrir dans un nouvel onglet"
                        >
                          <FiExternalLink className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="min-h-[56px]">
                      <p className="line-clamp-2 text-sm font-bold leading-5 text-slate-900 dark:text-white">
                        {item.title || "(Sans titre)"}
                      </p>

                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          {item.category || "Sans catégorie"}
                        </span>

                        {item.size_bytes && (
                          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            {formatFileSize(item.size_bytes)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-200 pt-3 dark:border-slate-800">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                          {formatDate(item.created_at)}
                        </p>
                        <p className="mt-0.5 truncate text-[10px] text-slate-400">
                          {item.original_name || item.file_name}
                        </p>
                      </div>

                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => onPreview(item)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                          title="Prévisualiser"
                        >
                          <FiEye className="h-4 w-4" />
                        </button>

                        <button
                          type="button"
                          onClick={() => onEdit(item)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                          title="Modifier"
                        >
                          <FiEdit2 className="h-4 w-4" />
                        </button>

                        <button
                          type="button"
                          onClick={() => onAskDelete(item.id)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-600 transition hover:bg-red-100 dark:border-red-950 dark:bg-red-950/30 dark:text-red-300"
                          title="Supprimer"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && total > 0 && (
          <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between">
            <div>
              Page <span className="font-bold text-slate-900 dark:text-white">{page}</span> /{" "}
              <span className="font-bold text-slate-900 dark:text-white">{totalPages}</span> —{" "}
              {total} média{total > 1 ? "s" : ""}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={gotoPrev}
                disabled={page <= 1}
                className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Précédent
              </button>

              <button
                type="button"
                onClick={gotoNext}
                disabled={page >= totalPages}
                className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}